import React, { useEffect, useState } from "react";
import API from "../../utils/API";
import Swal from "sweetalert2";
import { Link, useSearchParams } from "react-router-dom";
import GlobalSVG from "../../utils/GlobalSVG";
import moment from "moment-timezone";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { OsComments } from "../../components/OsComments";
import ServiceCategoryBadges from "../../components/serviceCategoryBadges";

/**
 * LocalStorage helpers
 */
const lsKeyForItem = (osId, serviceItemId) =>
    `yc_os_${osId}_item_${serviceItemId}_parts`;

const readPartsStateFromLS = (osId, serviceItemId) => {
    try {
        const raw = localStorage.getItem(lsKeyForItem(osId, serviceItemId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
    } catch { }
    return null;
};

const writePartsStateToLS = (osId, serviceItemId, partsArr) => {
    try {
        localStorage.setItem(lsKeyForItem(osId, serviceItemId), JSON.stringify(partsArr));
    } catch { }
};

/**
 * Robust extraction of "peças" from different shapes:
 */
// Returns an array of { name, service_parts_id, received, installed } objects.
// Falls back to a name-only entry if no service_parts_id is available.
const extractPartsList = (item) => {
    const buildLabel = (desc, code) => `${(desc || "").trim()}${(code || "").trim() ? ` (${(code || "").trim()})` : ""}`.trim();

    const arr = item?.service_items?.pecas;
    if (Array.isArray(arr) && arr.length) {
        const out = [];
        for (const p of arr) {
            if (typeof p === "string") {
                const t = p.trim();
                if (t) out.push({ name: t, service_parts_id: null, received: false, installed: false });
            } else if (p && typeof p === "object") {
                const label = buildLabel(p.descricao || p.description, p.codigo || p.code);
                if (label) {
                    out.push({
                        name: label,
                        service_parts_id: p.service_parts_id ?? null,
                        received: !!p.received,
                        installed: !!p.installed,
                    });
                }
            }
        }
        if (out.length) return out;
    }

    const grouped = item?.parts;
    if (Array.isArray(grouped) && grouped.length && grouped[0]?.options) {
        const out = [];
        grouped.forEach((group) => {
            (group.options || []).forEach((opt) => {
                const label = buildLabel(opt.description, opt.code);
                if (label) {
                    out.push({
                        name: label,
                        service_parts_id: opt.service_parts_id ?? null,
                        received: !!opt.received,
                        installed: !!opt.installed,
                    });
                }
            });
        });
        if (out.length) return out;
    }

    const rawStrCandidates = [
        item?.service_items?.pecas,
        item?.service_items?.selectedPecaString,
        item?.selected_peca,
        item?.pecas,
        item?.parts,
    ].filter((x) => typeof x === "string" && x.trim().length > 0);

    for (const raw of rawStrCandidates) {
        const split = raw
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        if (split.length) return split.map(n => ({ name: n, service_parts_id: null, received: false, installed: false }));
    }

    return [];
};

/** Normalize media urls */
const normalizeUrl = (it) => {
    if (typeof it === "string") return it;
    return it?.image_url || it?.audio_url || it?.url || "";
};

/** Normalize comments from various shapes */
const normalizeComment = (c) => {
    if (typeof c === "string") {
        return {
            id: Math.random().toString(36).slice(2),
            text: c,
            created_at: null,
            author: null,
        };
    }
    const text =
        c?.observation ??
        c?.description ??
        c?.comment ??
        c?.text ??
        c?.title ??
        "";

    return {
        id: c?.service_comments_id ?? c?.id ?? Math.random().toString(36).slice(2),
        text,
        created_at: c?.created_at || c?.updated_at || null,
        author: c?.user_name || c?.author || null,
    };
};

const Section = ({ title, children }) => (
    <div data-pdf-block className="flex flex-col w-full mb-8 rounded-lg overflow-hidden shadow-md bg-white">
        <div className="bg-blue-50 px-6 py-4 border-b border-blue-100">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-800">
                {title}
            </h2>
        </div>
        <div className="p-4 md:p-6">{children}</div>
    </div>
);

const ClosureButton = ({ label, active, at, onClick, busy }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={busy}
        title={active && at ? `Marcado em ${new Date(at).toLocaleString("pt-BR")}` : "Clique para marcar"}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${
            active
                ? "bg-green-600 border-green-600 text-white hover:bg-green-700"
                : "bg-white border-gray-300 text-gray-600 hover:border-green-500 hover:text-green-700"
        } ${busy ? "opacity-60 cursor-wait" : ""}`}
    >
        <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full border-2 ${active ? "bg-white border-white" : "border-gray-400"}`}>
            {active && (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
            )}
        </span>
        {label}
    </button>
);

const DataRow = ({ label, value }) => (
    <div className="flex flex-col md:flex-row py-2 border-b border-gray-100">
        <div className="w-full md:w-1/3 font-medium text-gray-600">{label}:</div>
        <div className="w-full md:w-2/3 text-gray-900">{value || "-"}</div>
    </div>
);

function OrderPage() {
    const [data, setData] = useState({});
    const [serviceItems, setServiceItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams] = useSearchParams();
    const [user, setUser] = useState(null);
    const id = searchParams.get("id");

    // substatus drives the workflow CTA visibility; derivedStatus is the
    // human-readable pipeline stage shown in the header (auto, no manual edit).
    const [substatus, setSubstatus] = useState("");
    const [derivedStatus, setDerivedStatus] = useState("");

    // OS-level comments
    const [osComments, setOsComments] = useState([]);
    const [newCommentText, setNewCommentText] = useState("");
    const [savingComment, setSavingComment] = useState(false);

    // Piece request modal state
    const [reqModal, setReqModal] = useState({ open: false, item: null });
    const [reqForm, setReqForm] = useState({ name: "", qty: 1, reason: "" });
    const [reqSubmitting, setReqSubmitting] = useState(false);

    // Pago / Retirado
    const [closureBusy, setClosureBusy] = useState(null);

    const toggleClosure = (what, value) => {
        const label = what === "paid" ? "pagamento" : "retirada";
        setClosureBusy(what);
        API.setServiceClosure(API.cancelToken(), id, what, value)
            .then((res) => {
                setData((prev) => ({
                    ...prev,
                    paid: res.data.paid,
                    picked_up: res.data.picked_up,
                }));
                if (res.data.closed) {
                    Swal.fire({
                        title: "OS encerrada",
                        text: "Pagamento e retirada confirmados. A ordem de serviço foi finalizada.",
                        icon: "success",
                        confirmButtonText: "Ok",
                    }).then(() => fetchOrderData(id));
                } else {
                    fetchOrderData(id);
                }
            })
            .catch((err) => {
                const msg = err?.response?.data?.error || `Não foi possível registrar o ${label}.`;
                Swal.fire("Erro", msg, "error");
            })
            .finally(() => setClosureBusy(null));
    };

    const openReqModal = (item) => {
        setReqModal({ open: true, item });
        setReqForm({ name: "", qty: 1, reason: "" });
    };

    const closeReqModal = () => setReqModal({ open: false, item: null });

    const submitPartRequest = async (e) => {
        e?.preventDefault?.();
        const name = reqForm.name.trim();
        const qty = Number(reqForm.qty);
        const reason = reqForm.reason.trim();

        if (!name || !qty || qty < 1) {
            Swal.fire("Campos obrigatórios", "Preencha Nome e Quantidade válida.", "warning");
            return;
        }

        setReqSubmitting(true);
        try {
            await API.requestPart(API.cancelToken(), {
                service_id: id,
                service_item_id: reqModal.item?.service_item_id,
                service_item_title: reqModal.item?.service_item_title,
                part_name: name,
                quantity: qty,
                reason,
            });
            closeReqModal();
            Swal.fire("Solicitação enviada", "Sua solicitação de peça foi registrada.", "success");
        } catch (error) {
            console.error("Error requesting part:", error);
            const msg = error?.response?.data?.error || "Erro ao enviar solicitação de peça.";
            Swal.fire("Erro", msg, "error");
        } finally {
            setReqSubmitting(false);
        }
    };

    // Bootstrap user
    useEffect(() => {
        let retries = 10;
        const interval = setInterval(() => {
            const storedUserJSON = localStorage.getItem("yc_usr_dt_ls");
            if (storedUserJSON) {
                try {
                    const parsedUser = JSON.parse(storedUserJSON);
                    setUser(parsedUser);
                    clearInterval(interval);
                } catch (error) {
                    console.error("Failed to parse yc_usr_dt_ls:", error);
                    clearInterval(interval);
                }
            } else {
                retries--;
                if (retries <= 0) clearInterval(interval);
            }
        }, 500);

        return () => clearInterval(interval);
    }, []);

    // Export PDF (full page)
    function exportPDF() {
        Swal.fire({
            title: "Gerando PDF...",
            text: "Por favor, aguarde enquanto o arquivo é gerado.",
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();

                setTimeout(() => {
                    const input = document.getElementById("divToPrint");
                    html2canvas(input, {
                        useCORS: true,
                        allowTaint: false,
                        scale: 2,
                        backgroundColor: "#ffffff",
                        windowWidth: input.scrollWidth,
                        windowHeight: input.scrollHeight,
                    }).then((canvas) => {
                        const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
                        const pdfWidth = pdf.internal.pageSize.getWidth();
                        const pdfHeight = pdf.internal.pageSize.getHeight();

                        // Fatiamos o canvas: uma imagem por página, cada uma com
                        // exatamente a altura do conteúdo que cabe nela.
                        //
                        // A implementação anterior reaproveitava a MESMA imagem alta
                        // em todas as páginas, deslocando o Y para cima. Isso deixava
                        // a última página com a imagem sobrando fora dos limites e,
                        // como o PNG carrega canal alfa, as regiões transparentes
                        // apareciam como uma faixa preta sobre o conteúdo em vários
                        // leitores de PDF. Fatiar + preencher de branco + gravar em
                        // JPEG (sem alfa) elimina a faixa de vez.
                        const sliceHeightPx = Math.floor((canvas.width * pdfHeight) / pdfWidth);

                        // Pontos de quebra permitidos: o topo de cada bloco marcado
                        // com data-pdf-block (seções e cards de serviço), na escala
                        // do canvas. Sem isso o corte cai em altura fixa e parte um
                        // card ao meio, que foi exatamente o que o cliente reportou.
                        const scale = canvas.height / input.scrollHeight;
                        const inputTop = input.getBoundingClientRect().top;
                        const breakpoints = Array.from(
                            input.querySelectorAll("[data-pdf-block]")
                        )
                            .map((el) => Math.round((el.getBoundingClientRect().top - inputTop) * scale))
                            .filter((y) => y > 0)
                            .sort((a, b) => a - b);

                        // Maior quebra que ainda cabe na página; se o bloco for mais
                        // alto que uma página inteira, corta na altura cheia mesmo.
                        const nextCut = (from) => {
                            const limit = from + sliceHeightPx;
                            if (limit >= canvas.height) return canvas.height;
                            let best = 0;
                            for (const y of breakpoints) {
                                if (y > from && y <= limit) best = y;
                                else if (y > limit) break;
                            }
                            return best > from ? best : limit;
                        };

                        let offsetY = 0;
                        let pageIndex = 0;

                        while (offsetY < canvas.height) {
                            const sliceH = nextCut(offsetY) - offsetY;

                            const pageCanvas = document.createElement("canvas");
                            pageCanvas.width = canvas.width;
                            pageCanvas.height = sliceH;

                            const ctx = pageCanvas.getContext("2d");
                            ctx.fillStyle = "#ffffff";
                            ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
                            ctx.drawImage(
                                canvas,
                                0, offsetY, canvas.width, sliceH,
                                0, 0, canvas.width, sliceH
                            );

                            if (pageIndex > 0) pdf.addPage();
                            pdf.addImage(
                                pageCanvas.toDataURL("image/jpeg", 0.92),
                                "JPEG",
                                0, 0,
                                pdfWidth,
                                (sliceH * pdfWidth) / canvas.width
                            );

                            offsetY += sliceH;
                            pageIndex += 1;
                        }

                        pdf.save(`OS-${id}.pdf`);
                        Swal.close();
                    }).catch((err) => {
                        console.error("[exportPDF] failed", err);
                        Swal.close();
                        Swal.fire("Erro", "Não foi possível gerar o PDF.", "error");
                    });
                }, 500);
            },
        });
    }


    // Load data + start/stop tracking
    useEffect(() => {
        if (!localStorage.getItem("yc_usr_dt_ls")) {
            window.location.href = "/login";
            return;
        }
        if (!id) return;

        fetchOrderData(id);
        fetchServiceItems(id);

        const onVisChange = () => {
            if (document.visibilityState === "hidden") {
                API.endServiceTracking(API.cancelToken(), id).catch(() => {
                    Swal.fire({
                        icon: "success",
                        title: "Tracking ended",
                        text: "The service time tracking has stopped.",
                        timer: 3000,
                        showConfirmButton: false,
                    });
                });
            }
        };
        const onBeforeUnload = () => {
            API.endServiceTracking(API.cancelToken(), id).catch(() => {
                Swal.fire({
                    icon: "success",
                    title: "Tracking ended",
                    text: "The service time tracking has stopped.",
                    timer: 3000,
                    showConfirmButton: false,
                });
            });
        };

        document.addEventListener("visibilitychange", onVisChange);
        window.addEventListener("beforeunload", onBeforeUnload);

        return () => {
            document.removeEventListener("visibilitychange", onVisChange);
            window.removeEventListener("beforeunload", onBeforeUnload);
            API.endServiceTracking(API.cancelToken(), id).catch(() => {
                Swal.fire({
                    icon: "success",
                    title: "Tracking ended",
                    text: "The service time tracking has stopped.",
                    timer: 3000,
                    showConfirmButton: false,
                });
            });
        };
    }, [id]);

    const fetchOrderData = (serviceId) => {
        setLoading(true);
        API.getOrder(API.cancelToken(), serviceId, true)
            .then((res) => {
                setData(res.data);

                // substatus (drives CTA visibility) + derived pipeline stage (header)
                const s = res.data?.sub_status || "";
                setSubstatus(s);
                setDerivedStatus(res.data?.derived_status || "");

                // comments (robust)
                const rawComments = Array.isArray(res.data?.comments)
                    ? res.data.comments
                    : Array.isArray(res.data?.service_comments)
                        ? res.data.service_comments
                        : [];
                setOsComments(rawComments.map(normalizeComment));

                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
                Swal.fire("Erro", "Erro ao carregar a ordem de serviço", "error");
            });
    };

    const fetchServiceItems = (serviceId) => {
        API.getServiceItems(API.cancelToken(), serviceId)
            .then((res) => setServiceItems(res.data))
            .catch((err) => console.log(err));
    };

    const translateStatus = (status) => {
        switch (status) {
            case "NOT_STARTED":
                return "Não Iniciado";
            case "IN_PROGRESS":
                return "Iniciado";
            case "FINISHED":
                return "Finalizado";
            default:
                return status;
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return moment(dateStr).add(3, "hours").format("DD/MM/YYYY HH:mm");
    };

    const formatCurrency = (value) => {
        if (!value && value !== 0) return "R$ 0,00";
        const numberFormat = new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
        });
        return numberFormat.format(value);
    };

    const handleStart = (itemId) => {
        // Ask the mechanic to confirm receipt of the parts before starting.
        const itemParts = partsByItem[itemId] || [];
        const dbParts = itemParts.filter((p) => p.service_parts_id != null);
        const received = dbParts.filter((p) => p.received);
        const waiting = dbParts.filter((p) => !p.received);

        const receivedHtml = received.length
            ? `<p style="margin-top:6px"><strong>Peças recebidas (${received.length}):</strong></p><ul style="text-align:left;padding-left:20px">${received.map(p => `<li>${p.name}</li>`).join('')}</ul>`
            : '';
        const waitingHtml = waiting.length
            ? `<p style="margin-top:6px;color:#b45309"><strong>Ainda aguardando (${waiting.length}):</strong></p><ul style="text-align:left;padding-left:20px;color:#b45309">${waiting.map(p => `<li>${p.name}</li>`).join('')}</ul>`
            : '';
        const bodyHtml = dbParts.length
            ? `${receivedHtml}${waitingHtml}<p style="margin-top:10px">Confirmo o recebimento das peças disponíveis e o início do serviço.</p>`
            : `<p>Tem certeza que deseja iniciar este item?</p>`;

        Swal.fire({
            title: "Confirmar recebimento e iniciar",
            html: bodyHtml,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#389E0D",
            confirmButtonText: "Confirmar e iniciar",
            cancelButtonText: "Cancelar",
        }).then((result) => {
            if (result.isConfirmed) {
                // Marca o início da execução da OS. O backend já expunha este
                // endpoint, mas nada o chamava — só o de término — então
                // tracking_started_at ficava sempre nulo e o tempo médio do
                // dashboard dava 0h. O backend usa COALESCE, então só o
                // primeiro item iniciado grava a data.
                API.startServiceTracking(API.cancelToken(), id).catch((err) =>
                    console.error("[handleStart] tracking start falhou", err)
                );
                API.startServiceItem(API.cancelToken(), itemId).then(() =>
                    fetchServiceItems(id)
                );
            }
        });
    };

    const handleFinish = (itemId) => {
        Swal.fire({
            title: "Finalizar serviço",
            text: "Tem certeza que deseja finalizar este item?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sim, finalizar",
        }).then((result) => {
            if (result.isConfirmed) {
                API.finishServiceItem(API.cancelToken(), itemId).then(() =>
                    fetchServiceItems(id)
                );
            }
        });
    };

    const handleFinishAll = () => {
        const pending = (serviceItems || []).filter(
            (it) => it.status === "IN_PROGRESS" || it.status === "NOT_STARTED"
        );
        if (pending.length === 0) {
            Swal.fire("Nenhum serviço pendente", "Todos os itens já estão finalizados.", "info");
            return;
        }

        Swal.fire({
            title: "Finalizar todos os serviços",
            text: `Finalizar ${pending.length} item(ns)? Esta ação não pode ser desfeita.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sim, finalizar todos",
            cancelButtonText: "Cancelar",
        }).then(async (result) => {
            if (!result.isConfirmed) return;

            Swal.fire({
                title: "Finalizando...",
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading(),
            });

            try {
                await Promise.all(
                    pending.map((it) =>
                        API.finishServiceItem(API.cancelToken(), it.service_item_id)
                    )
                );
                fetchServiceItems(id);
                Swal.fire("Finalizados!", "Todos os serviços foram finalizados.", "success");
            } catch (err) {
                console.error(err);
                fetchServiceItems(id);
                Swal.fire("Erro", "Alguns serviços podem não ter sido finalizados.", "error");
            }
        });
    };

    const runTransition = (action, opts) => {
        const { title, text, confirmText, successTitle, successText, newSubstatus } = opts;
        Swal.fire({
            title, text, icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#389E0D",
            confirmButtonText: confirmText,
            cancelButtonText: "Cancelar",
        }).then((result) => {
            if (!result.isConfirmed) return;
            Swal.fire({ title: "Atualizando...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            API.serviceTransition(API.cancelToken(), id, action)
                .then((res) => {
                    const ns = res.data?.new_substatus || newSubstatus;
                    setSubstatus(ns);
                    // In the later pipeline stages the derived label equals the
                    // substatus, so keep the header in sync immediately.
                    setDerivedStatus(ns);
                    setData((d) => ({ ...d, sub_status: ns, substatus: ns }));
                    Swal.fire(successTitle, successText, "success");
                })
                .catch((err) => {
                    console.error(`[transition:${action}] failed`, err);
                    const status = err?.response?.status;
                    const body = err?.response?.data;
                    const msg = body?.error || body?.message || err?.message || "Não foi possível atualizar.";
                    Swal.fire(
                        "Erro",
                        `${msg}${status ? `\n(HTTP ${status})` : ""}${body?.current_substatus ? `\nSub-status atual: ${body.current_substatus}` : ""}`,
                        "error"
                    );
                });
        });
    };

    const handleReleaseToTest = () => runTransition("release_to_test", {
        title: "Enviar para teste?",
        text: "O serviço será enviado para o chefe da oficina realizar o teste.",
        confirmText: "Enviar para teste",
        successTitle: "Enviado!",
        successText: "O serviço foi enviado para o chefe da oficina.",
        newSubstatus: "DISPONÍVEL PARA TESTE",
    });

    const handleReleaseToPickup = () => runTransition("release_to_pickup", {
        title: "Liberar para pagamento e retirada?",
        text: "O cliente poderá ser avisado de que o veículo está pronto.",
        confirmText: "Liberar",
        successTitle: "Liberado!",
        successText: "O serviço está disponível para retirada e pagamento.",
        newSubstatus: "DISPONÍVEL PARA RETIRADA E PAGAMENTO",
    });

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case "FINISHED":
                return "bg-green-600 text-white";
            case "NOT_STARTED":
                return "text-black";
            case "IN_PROGRESS":
                return "text-black";
            default:
                return "bg-gray-300 text-black";
        }
    };

    // Parts checklist state
    // Each entry: { name, service_parts_id, received, installed, checked (legacy LS fallback) }
    const [partsByItem, setPartsByItem] = useState({});
    useEffect(() => {
        if (!id) return;
        const next = {};
        (serviceItems || []).forEach((it) => {
            const itemId = it?.service_item_id;
            const baseParts = extractPartsList(it);
            const saved = readPartsStateFromLS(id, itemId);
            const merged = baseParts.map((bp) => {
                // For DB-backed parts, use the server's installed flag.
                // For legacy parts (no service_parts_id), fall back to localStorage.
                if (bp.service_parts_id != null) {
                    return { ...bp, checked: bp.installed };
                }
                const prev = Array.isArray(saved) ? saved.find((p) => p?.name === bp.name) : null;
                return { ...bp, checked: !!prev?.checked };
            });
            if (Array.isArray(saved)) {
                saved.forEach((p) => {
                    if (p?.name && !merged.find((m) => m.name === p.name)) {
                        merged.push({ name: p.name, service_parts_id: null, received: false, installed: false, checked: !!p.checked });
                    }
                });
            }
            next[itemId] = merged;
        });
        setPartsByItem(next);
    }, [id, serviceItems]);

    const togglePartChecked = (serviceItemId, partName) => {
        const current = partsByItem?.[serviceItemId] || [];
        const part = current.find(p => p.name === partName);
        if (!part) return;
        const newChecked = !part.checked;

        // Optimistic local update
        setPartsByItem((prev) => {
            const cur = prev?.[serviceItemId] || [];
            const updated = cur.map((p) =>
                p.name === partName ? { ...p, checked: newChecked, installed: newChecked } : p
            );
            // Keep LS in sync for legacy parts so the UI survives reloads
            if (part.service_parts_id == null) writePartsStateToLS(id, serviceItemId, updated);
            return { ...prev, [serviceItemId]: updated };
        });

        // Persist to backend when we have a DB id
        if (part.service_parts_id != null) {
            API.setPartInstalled(API.cancelToken(), part.service_parts_id, newChecked).catch(() => {
                Swal.fire("Erro", "Não foi possível salvar a peça.", "error");
                // Revert
                setPartsByItem((prev) => {
                    const cur = prev?.[serviceItemId] || [];
                    const reverted = cur.map((p) =>
                        p.name === partName ? { ...p, checked: !newChecked, installed: !newChecked } : p
                    );
                    return { ...prev, [serviceItemId]: reverted };
                });
            });
        }
    };

    const markAll = (serviceItemId, value) => {
        const current = partsByItem?.[serviceItemId] || [];
        // Optimistic update
        setPartsByItem((prev) => {
            const cur = prev?.[serviceItemId] || [];
            const updated = cur.map((p) => ({ ...p, checked: value, installed: p.service_parts_id != null ? value : p.installed }));
            const legacyOnly = updated.filter(p => p.service_parts_id == null);
            if (legacyOnly.length) writePartsStateToLS(id, serviceItemId, updated);
            return { ...prev, [serviceItemId]: updated };
        });
        // Persist DB-backed ones
        current.forEach((p) => {
            if (p.service_parts_id != null && p.checked !== value) {
                API.setPartInstalled(API.cancelToken(), p.service_parts_id, value).catch(() => {});
            }
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <p>Carregando...</p>
            </div>
        );
    }

    const osImages = Array.isArray(data.images)
        ? data.images.map(normalizeUrl).filter(Boolean)
        : [];
    const osAudios = Array.isArray(data.audios)
        ? data.audios.map(normalizeUrl).filter(Boolean)
        : [];

    const isMechanic = user?.role === "MECANICO";

    const addOsComment = async () => {
        const text = newCommentText.trim();
        if (!text) {
            Swal.fire("Campo obrigatório", "Escreva um comentário antes de enviar.", "warning");
            return;
        }

        try {
            setSavingComment(true);
            Swal.fire({
                title: "Enviando comentário...",
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading(),
            });

            await API.postComment(API.cancelToken(), id, text);

            // Optimistic update
            const appended = {
                id: Math.random().toString(36).slice(2),
                text,
                created_at: new Date().toISOString(),
                author: user?.name || "Mecânico",
            };
            setOsComments((prev) => [appended, ...prev]);
            setNewCommentText("");

            Swal.fire({
                icon: "success",
                title: "Comentário adicionado",
                timer: 1200,
                showConfirmButton: false,
            });
        } catch (e) {
            console.error(e);
            Swal.fire("Erro", "Não foi possível salvar o comentário.", "error");
        } finally {
            setSavingComment(false);
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen pb-10">
            <div className="container mx-auto px-4 py-6" id="divToPrint">
                {/* Status header — reflects the real pipeline stage automatically */}
                <div className="container mx-auto mb-4 pt-6">
                    <div className="bg-white p-4 md:p-5 rounded-lg shadow-md flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="flex flex-col md:flex-row md:items-center gap-3">
                            <div>
                                <p className="text-sm text-gray-600">Status Atual</p>
                                <p className="text-lg font-semibold text-gray-900">
                                    {derivedStatus || substatus || "—"}
                                </p>
                            </div>
                            {substatus === "DISPONÍVEL PARA RETIRADA E PAGAMENTO" && data.client?.phone_number && (
                                <a
                                    href={`https://wa.me/${String(data.client.phone_number).replace(/\D/g, "").replace(/^0+/, "")}?text=${encodeURIComponent(
                                        `Olá ${data.client?.name || ""}, tudo bem? Seu veículo${data.vehicle?.license_plate ? ` de placa ${data.vehicle.license_plate}` : ""} já está disponível para retirada e pagamento na oficina. Aguardamos você!`
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#25D366] text-white text-sm font-semibold hover:bg-[#1ebe57] transition-colors shadow-sm"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M20.52 3.48A11.9 11.9 0 0012.04 0C5.46 0 .1 5.35.1 11.93c0 2.1.55 4.16 1.6 5.97L0 24l6.26-1.64a11.93 11.93 0 005.77 1.47h.01c6.58 0 11.93-5.35 11.93-11.93 0-3.19-1.24-6.19-3.45-8.42zM12.04 21.8h-.01a9.86 9.86 0 01-5.03-1.38l-.36-.21-3.72.97.99-3.62-.24-.37a9.86 9.86 0 01-1.51-5.26c0-5.47 4.45-9.92 9.92-9.92 2.65 0 5.14 1.03 7.01 2.91a9.86 9.86 0 012.9 7.01c0 5.47-4.45 9.91-9.95 9.91zm5.44-7.42c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.05 1.02-1.05 2.5 0 1.47 1.07 2.9 1.22 3.1.15.2 2.11 3.22 5.11 4.52.71.31 1.27.49 1.7.62.72.23 1.37.2 1.88.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z"/>
                                    </svg>
                                    Avisar Cliente no WhatsApp
                                </a>
                            )}
                        </div>

                        {/* Encerramento da OS — Pago e Retirado.
                            Registrar os dois separadamente, com data e autor, é o
                            que protege a oficina se algo acontecer com o veículo
                            depois da saída (multas, retirada por terceiros). A OS
                            só é encerrada quando ambos estiverem marcados. */}
                        {["RECEPCAO", "ADMIN", "BOSS"].includes(user?.role) && (
                            <div className="flex flex-wrap items-center gap-2">
                                <ClosureButton
                                    label="Pago"
                                    active={!!data.paid}
                                    at={data.paid_at}
                                    onClick={() => toggleClosure("paid", !data.paid)}
                                    busy={closureBusy === "paid"}
                                />
                                <ClosureButton
                                    label="Retirado"
                                    active={!!data.picked_up}
                                    at={data.picked_up_at}
                                    onClick={() => toggleClosure("picked_up", !data.picked_up)}
                                    busy={closureBusy === "picked_up"}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Header with Company Info */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 bg-white p-6 rounded-lg shadow-md">
                    <div className="flex items-center mb-4 md:mb-0">
                        <div className="flex w-16 md:w-20 mr-4">
                            {GlobalSVG.exampleProfile()}
                        </div>
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                                Empresa Teste LTDA
                            </h1>
                            <p className="text-sm text-gray-600">CNPJ: 16.221.324/0001-90</p>
                        </div>
                    </div>

                    <button
                        onClick={exportPDF}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-all duration-200 shadow-sm"
                    >
                        {GlobalSVG.download()}
                        <span>Baixar PDF</span>
                    </button>
                </div>

                {/* Service categories chosen at intake */}
                {(() => {
                    const codes = data?.service_categories;
                    const hasCodes = Array.isArray(codes) ? codes.length > 0 : !!codes;
                    if (!hasCodes) return null;
                    return (
                        <Section title="Tipo de Serviço">
                            <ServiceCategoryBadges codes={codes} />
                        </Section>
                    );
                })()}

                {/* Client Info — hidden from mechanic and workshop chief (BOSS) */}
                {user && user.role !== "MECANICO" && user.role !== "BOSS" && (
                    <Section title="Informações do Cliente">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <DataRow label="Nome" value={data.client?.name} />
                            <DataRow label="CPF" value={data.client?.cpf} />
                            <DataRow label="Telefone" value={data.client?.phone_number} />
                            <DataRow label="E-mail" value={data.client?.email} />
                            <DataRow label="CEP" value={data.client?.ep} />
                            <DataRow label="Endereço" value={data.client?.address_street} />
                            <DataRow label="Número" value={data.client?.address_number} />
                            <DataRow label="Complemento" value={data.client?.address_extra_info} />
                            <DataRow label="Bairro" value={data.client?.address_neighborhood} />
                            <DataRow
                                label="Município"
                                value={`${data.client?.address_city} ${data.client?.address_state ? "- " + data.client?.address_state : ""
                                    }`}
                            />
                        </div>
                    </Section>
                )}

                {/* Vehicle Info */}
                <Section title="Informações do Veículo">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <DataRow label="Marca/Modelo" value={data.vehicle?.model} />
                        <DataRow label="Ano/Modelo" value={data.vehicle_year} />
                        <DataRow label="Cor" value={data.vehicle?.color} />
                        <DataRow label="Combustível" value={data.vehicle?.fuel_type} />
                        <DataRow label="Procedência" value={data.vehicle?.nationality} />
                        <DataRow label="Município" value={data.vehicle?.city} />
                        <DataRow label="Blindado" value={data.vehicle?.armored ? "Sim" : "Não"} />
                    </div>
                </Section>

                {/* OS-level Evidence (Images + Audios) */}
                {(osImages.length > 0 || osAudios.length > 0) && (
                    <Section title="Arquivos do Check-In (OS)">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Images */}
                            {osImages.length > 0 && (
                                <div>
                                    <h4 className="font-semibold text-gray-800 mb-2">Imagens</h4>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {osImages.map((src, i) => (
                                            <div
                                                key={`os_img_${i}`}
                                                className="relative rounded overflow-hidden bg-gray-100 border"
                                                title={`Imagem ${i + 1}`}
                                            >
                                                <img
                                                    src={src}
                                                    crossOrigin="anonymous"
                                                    loading="lazy"
                                                    alt={`Imagem ${i + 1} - OS ${id}`}
                                                    className="w-full h-32 object-cover"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Audios */}
                            {osAudios.length > 0 && (
                                <div>
                                    <h4 className="font-semibold text-gray-800 mb-2">Áudios</h4>
                                    <ul className="space-y-2">
                                        {osAudios.map((url, i) => (
                                            <li
                                                key={`os_aud_${i}`}
                                                className="flex items-center gap-3 bg-white border rounded p-2"
                                            >
                                                <span className="text-sm text-gray-500 shrink-0">Áudio {i + 1}</span>
                                                <audio className="w-full" controls preload="none" src={url}>
                                                    Seu navegador não suporta áudio.
                                                </audio>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </Section>
                )}

                <Section title="Comentários da OS">
                    <OsComments
                        serviceId={id}
                        user={user}
                        initialComments={
                            Array.isArray(data?.comments)
                                ? data.comments
                                : Array.isArray(data?.service_comments)
                                    ? data.service_comments
                                    : []
                        }
                        postCommentApi={(serviceId, text) => API.postComment(API.cancelToken(), serviceId, text)}
                    />
                </Section>

                {/* Service Items */}
                <Section title="Serviços Necessários">
                    <div className="overflow-x-auto">
                        {serviceItems.some(
                            (it) => it.status === "IN_PROGRESS" || it.status === "NOT_STARTED"
                        ) && (
                            <div className="flex justify-end mb-4">
                                <button
                                    type="button"
                                    onClick={handleFinishAll}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    Finalizar todos os serviços
                                </button>
                            </div>
                        )}
                        <div className="grid gap-4">
                            {serviceItems.length > 0 ? (
                                serviceItems.map((item) => {
                                    const itemParts = partsByItem[item.service_item_id] || [];
                                    const usedCount = itemParts.filter((p) => p.checked).length;
                                    return (
                                        <div
                                            key={item.service_item_id}
                                            data-pdf-block
                                            className="bg-white p-4 rounded-md shadow-md"
                                        >
                                            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-2">
                                                <div>
                                                    <h3 className="text-lg font-bold text-gray-800">
                                                        {item.service_item_title}
                                                    </h3>
                                                    <p className="text-sm text-gray-600">
                                                        {item.service_item_annotation || "-"}
                                                    </p>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2 mt-2 md:mt-0">
                                                    <span
                                                        className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadgeColor(
                                                            item.status
                                                        )}`}
                                                    >
                                                        {translateStatus(item.status)}
                                                    </span>
                                                    {item.status === "NOT_STARTED" && (
                                                        <button
                                                            onClick={() => handleStart(item.service_item_id)}
                                                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                                                        >
                                                            Iniciar
                                                        </button>
                                                    )}
                                                    {item.status === "IN_PROGRESS" && (
                                                        <button
                                                            onClick={() => handleFinish(item.service_item_id)}
                                                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                                                        >
                                                            Finalizar
                                                        </button>
                                                    )}
                                                    {item.status === "FINISHED" && (
                                                        <span className="text-green-700 font-semibold text-sm">
                                                            Finalizado ✅
                                                        </span>
                                                    )}

                                                    <button
                                                        onClick={() => openReqModal(item)}
                                                        className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                                                    >
                                                        Solicitar Peça
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Meta info */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-700 mb-3">
                                                <div>
                                                    <strong>OS:</strong> {id}
                                                </div>
                                                <div>
                                                    <strong>Início:</strong> {formatDate(item.started_at)}
                                                </div>
                                                <div>
                                                    <strong>Término:</strong> {formatDate(item.finished_at)}
                                                </div>
                                                <div>
                                                    <strong>Tempo Execução:</strong>{" "}
                                                    {item.started_at && item.finished_at
                                                        ? `${Math.round(
                                                            (new Date(item.finished_at) - new Date(item.started_at)) / 60000
                                                        )} min`
                                                        : "-"}
                                                </div>
                                                <div>
                                                    <strong>Responsável:</strong>{" "}
                                                    {item.started_by_user_name || "-"}
                                                </div>
                                                <div>
                                                    <strong>Peças usadas:</strong>{" "}
                                                    {usedCount}/{itemParts.length}
                                                </div>
                                            </div>

                                            {/* Peças utilizadas checklist */}
                                            <div className="border rounded-md p-3 bg-gray-50">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h4 className="font-semibold text-gray-800">
                                                        Peças utilizadas
                                                    </h4>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => markAll(item.service_item_id, true)}
                                                            className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                                                        >
                                                            Marcar todas
                                                        </button>
                                                        <button
                                                            onClick={() => markAll(item.service_item_id, false)}
                                                            className="text-xs px-2 py-1 rounded bg-gray-300 text-gray-800 hover:bg-gray-400"
                                                        >
                                                            Limpar
                                                        </button>
                                                    </div>
                                                </div>

                                                {itemParts.length > 0 ? (
                                                    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                        {itemParts.map((p) => (
                                                            <li
                                                                key={`${item.service_item_id}_${p.name}`}
                                                                className="flex items-start gap-2 bg-white rounded border p-2"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    className="mt-1 h-4 w-4"
                                                                    checked={!!p.checked}
                                                                    onChange={() =>
                                                                        togglePartChecked(
                                                                            item.service_item_id,
                                                                            p.name
                                                                        )
                                                                    }
                                                                />
                                                                <div className="flex-1 min-w-0">
                                                                    <span
                                                                        className={`text-sm select-none ${p.checked ? "line-through text-gray-500" : "text-gray-800"}`}
                                                                    >
                                                                        {p.name}
                                                                    </span>
                                                                    {p.service_parts_id != null && (
                                                                        <span
                                                                            className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                                                                p.received
                                                                                    ? "bg-green-100 text-green-800"
                                                                                    : "bg-yellow-100 text-yellow-800"
                                                                            }`}
                                                                        >
                                                                            {p.received ? "Recebida" : "Aguardando"}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="text-sm text-gray-500">
                                                        Nenhuma peça informada para este serviço.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-center text-gray-500">
                                    Nenhum item de serviço encontrado.
                                </p>
                            )}
                        </div>
                    </div>
                </Section>

                {/* Workflow CTAs (visible per role + state) */}
                {(() => {
                    const role = user?.role;
                    const allServicesFinished = serviceItems.length > 0 && serviceItems.every(it => it.status === "FINISHED");
                    const canMechanicRelease = role === "MECANICO" || role === "ADMIN" || role === "BOSS";
                    const canBossRelease = role === "BOSS" || role === "ADMIN";
                    const canAssignMechanic = role === "BOSS" || role === "ADMIN";

                    // Pre-test states: the mechanic can send the service to test from any
                    // state that comes before the test stage.
                    const PRE_TEST_STATUSES = ["AGUARDANDO PEÇA", "EM REPARO", "", null, undefined];
                    const inPreTestState = PRE_TEST_STATUSES.includes(substatus);

                    const hasAssignedMechanic = !!data?.assigned_to;
                    const showAssignMechanic = canAssignMechanic && substatus === "EM REPARO" && !hasAssignedMechanic;
                    const showReleaseToTest = canMechanicRelease && inPreTestState && allServicesFinished;
                    const showReleaseToPickup = canBossRelease && substatus === "DISPONÍVEL PARA TESTE";

                    if (!showAssignMechanic && !showReleaseToTest && !showReleaseToPickup) return null;

                    return (
                        <div className="mb-8 bg-white rounded-lg shadow-md p-6 flex flex-col sm:flex-row items-center justify-end gap-3">
                            {showAssignMechanic && (
                                <Link
                                    to="/pending/assignments"
                                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white text-sm font-semibold rounded-lg hover:bg-amber-600 transition-colors shadow-sm"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                                    </svg>
                                    Atribuir Mecânico
                                </Link>
                            )}
                            {showReleaseToTest && (
                                <button
                                    type="button"
                                    onClick={handleReleaseToTest}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                                >
                                    Enviar para Chefe da Oficina (Teste)
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            )}
                            {showReleaseToPickup && (
                                <button
                                    type="button"
                                    onClick={handleReleaseToPickup}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#389E0D] text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                                >
                                    Liberar para Pagamento e Retirada
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    );
                })()}

                {/* Additional Info */}
                <Section title="Informações Adicionais">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-gray-50 p-4 rounded-md">
                            <p className="text-sm text-gray-500 mb-1">Quilometragem</p>
                            <p className="text-xl font-medium">{data.kms} KM</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-md">
                            <p className="text-sm text-gray-500 mb-1">Combustível</p>
                            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2 mb-1">
                                <div
                                    className="bg-blue-600 h-2.5 rounded-full"
                                    style={{ width: `${data.fuel}%` }}
                                ></div>
                            </div>
                            <p className="text-right text-sm">{Math.trunc(data.fuel)}%</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-md">
                            <p className="text-sm text-gray-500 mb-1">Data de Emissão</p>
                            <p className="text-xl font-medium">{formatDate(data.created_at)}</p>
                        </div>
                    </div>
                </Section>
            </div>

            {/* Solicitar Peça Modal */}
            {reqModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={closeReqModal}
                        aria-hidden="true"
                    />
                    {/* Modal */}
                    <div
                        className="relative z-10 w-[95%] max-w-lg rounded-lg bg-white shadow-xl"
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-800">
                                Solicitar Peça
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                                OS: {id} • Serviço:{" "}
                                <span className="font-medium">
                                    {reqModal.item?.service_item_title}
                                </span>
                            </p>
                        </div>

                        <form onSubmit={submitPartRequest} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nome da Peça <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={reqForm.name}
                                    onChange={(e) => setReqForm((f) => ({ ...f, name: e.target.value }))}
                                    className="w-full rounded border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Ex.: Filtro de óleo (Código XYZ)"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Quantidade <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={reqForm.qty}
                                    onChange={(e) => setReqForm((f) => ({ ...f, qty: e.target.value }))}
                                    className="w-32 rounded border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-gray-700 mb-1">
                                    Motivo
                                </label>
                                <textarea
                                    rows={3}
                                    value={reqForm.reason}
                                    onChange={(e) => setReqForm((f) => ({ ...f, reason: e.target.value }))}
                                    className="w-full rounded border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Explique a necessidade da peça (opcional)"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={closeReqModal}
                                    disabled={reqSubmitting}
                                    className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={reqSubmitting}
                                    className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {reqSubmitting && (
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                        </svg>
                                    )}
                                    {reqSubmitting ? "Enviando..." : "Enviar solicitação"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default OrderPage;
